<template>
  <div class="bg-white rounded-xl shadow-sm min-h-[500px] flex flex-col">
    <!-- Step Indicator -->
    <div class="p-6 border-b border-gray-100">
      <div class="flex items-center justify-between max-w-2xl mx-auto">
        <div v-for="(step, index) in steps" :key="index" class="flex flex-col items-center relative z-10">
          <div 
            class="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors duration-300"
            :class="[
              currentStep > index + 1 ? 'bg-green-500 text-white' : 
              currentStep === index + 1 ? 'bg-emerald-600 text-white ring-4 ring-emerald-100' : 'bg-gray-200 text-gray-500'
            ]"
          >
            <span v-if="currentStep > index + 1">✓</span>
            <span v-else>{{ index + 1 }}</span>
          </div>
          <span class="mt-2 text-xs font-medium" :class="currentStep >= index + 1 ? 'text-gray-800' : 'text-gray-400'">
            {{ step }}
          </span>
        </div>
        <!-- Progress Bar Background -->
        <div class="absolute top-4 left-0 w-full h-0.5 bg-gray-200 -z-0 hidden md:block"></div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flex-1 p-6 w-full max-w-5xl mx-auto">
      
      <!-- Step 1: Select Clinic (Equivalent to Step 2 in Doctor View) -->
      <div v-if="currentStep === 1" class="space-y-6 animate-fade-in">
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold text-gray-800">请选择您需要咨询的专科</h2>
          <p class="text-gray-500 mt-2">选择对应症状的科室，我们将为您提供专业的问诊服务</p>
        </div>

        <div v-if="loadingClinics" class="text-center py-20 text-gray-500">
          <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          正在加载科室列表...
        </div>

        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div 
            v-for="clinic in clinics" 
            :key="clinic.id"
            @click="selectClinic(clinic)"
            class="p-6 border rounded-xl cursor-pointer hover:border-emerald-500 hover:shadow-lg transition-all duration-300 relative group bg-white"
            :class="selectedClinic?.id === clinic.id ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' : 'border-gray-100'"
          >
            <div class="flex items-start space-x-4">
              <div class="p-3 bg-emerald-100 rounded-lg text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <div>
                <h3 class="font-bold text-lg text-gray-800 mb-2 group-hover:text-emerald-700">{{ clinic.name }}</h3>
                <p class="text-sm text-gray-500 leading-relaxed line-clamp-2">{{ clinic.description }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="flex justify-center mt-10">
          <button 
            @click="startDiagnosisProcess" 
            :disabled="!selectedClinic"
            class="bg-emerald-600 text-white px-10 py-3 rounded-full font-medium hover:bg-emerald-700 transition shadow-lg shadow-emerald-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none transform hover:-translate-y-0.5 active:translate-y-0"
          >
            开始问诊
          </button>
        </div>
      </div>

      <!-- Step 2: Diagnosis Questionnaire -->
      <div v-if="currentStep === 2" class="max-w-3xl mx-auto animate-fade-in">
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <!-- Question Header -->
          <div class="bg-gradient-to-r from-emerald-50 to-white p-8 border-b border-gray-100">
            <div class="flex justify-between items-center mb-4">
              <span class="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">
                问题 {{ questionCount }}
              </span>
              <!-- <span class="text-xs text-gray-400">会话ID: {{ sessionId }}</span> -->
            </div>
            
            <div v-if="loadingQuestion" class="py-4">
              <div class="h-6 bg-gray-100 rounded w-3/4 animate-pulse mb-2"></div>
              <div class="h-6 bg-gray-100 rounded w-1/2 animate-pulse"></div>
            </div>
            
            <h3 v-else class="text-2xl font-bold text-gray-800 leading-relaxed">
              {{ currentQuestion?.text }}
            </h3>
          </div>

          <!-- Options -->
          <div class="p-8 bg-white">
            <div v-if="loadingQuestion" class="space-y-4">
              <div v-for="i in 3" :key="i" class="h-14 bg-gray-50 rounded-xl animate-pulse"></div>
            </div>

            <div v-else class="space-y-4">
              <button 
                v-for="option in currentQuestion?.options" 
                :key="option.value"
                @click="submitAnswer(option.value)"
                class="w-full text-left p-5 border border-gray-200 rounded-xl hover:border-emerald-500 hover:bg-emerald-50 transition-all duration-200 flex justify-between items-center group relative overflow-hidden"
              >
                <span class="text-gray-700 font-medium group-hover:text-emerald-800 z-10 relative">{{ option.label }}</span>
                <span class="w-5 h-5 border-2 border-gray-300 rounded-full group-hover:border-emerald-500 z-10 relative flex items-center justify-center">
                  <span class="w-2.5 h-2.5 bg-emerald-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></span>
                </span>
              </button>
            </div>
            
            <div class="mt-8 flex justify-between items-center border-t border-gray-100 pt-6">
              <button 
                v-if="questionCount > 1"
                @click="rollbackToPrevQuestion"
                class="text-gray-400 hover:text-gray-600 text-sm flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                </svg>
                回到上一题
              </button>
              <button 
                v-if="questionCount > 1"
                @click="handleRestart"
                class="text-gray-400 hover:text-gray-600 text-sm flex items-center ml-auto"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                重新开始
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 3: Diagnosis Result -->
      <div v-if="currentStep === 3" class="animate-fade-in">
        <div class="max-w-4xl mx-auto">
          <div class="bg-white border border-gray-100 rounded-2xl shadow-lg overflow-hidden mb-8">
            <div class="bg-emerald-600 p-8 text-white text-center">
              <div class="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-600 shadow-md">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 class="text-2xl font-bold mb-2">问诊完成</h2>
              <p class="opacity-90"></p>
            </div>

            <div class="p-8">
               <div class="space-y-6">
                 <!-- 诊断名称 -->
                 <div class="flex items-start pb-6 border-b border-gray-100">
                   <div class="flex-shrink-0 w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 mr-4">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                     </svg>
                   </div>
                   <div>
                     <h4 class="text-sm font-bold text-gray-500 uppercase tracking-wider mb-1">初步诊断</h4>
                     <p class="text-2xl font-bold text-gray-800">{{ diagnosisResult?.disease || '未明确诊断' }}</p>
                   </div>
                 </div>
                 
                 <!-- 健康建议 -->
                 <div class="flex items-start pb-6 border-b border-gray-100">
                   <div class="flex-shrink-0 w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 mr-4">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                       <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                     </svg>
                   </div>
                   <div class="flex-1">
                     <h4 class="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2">健康建议</h4>
                     <div class="bg-gray-50 p-4 rounded-xl text-gray-700 leading-relaxed whitespace-pre-line border border-gray-100">
                       请将问诊过程内容，提供给您的机构，以获取更准确的建议。
                     </div>
                   </div>
                 </div>
               </div>
            </div>
            
            <!-- 舌苔图片上传 -->
            <div class="px-8 pb-8">
              <div class="w-full rounded-2xl p-4 shadow-sm border bg-white border-gray-100 space-y-4">
                <div 
                  class="flex justify-between items-center cursor-pointer"
                  @click="is_tongue_upload_expanded = !is_tongue_upload_expanded"
                >
                  <div class="flex items-center space-x-2">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span class="font-medium text-gray-700">上传舌苔图片 (可选)</span>
                  </div>
                  <svg 
                    :class="{'rotate-180': is_tongue_upload_expanded}" 
                    class="w-5 h-5 text-gray-500 transition-transform" 
                    fill="none" viewBox="0 0 24 24" stroke="currentColor"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                  </svg>
                </div>

                <div v-show="is_tongue_upload_expanded" class="pt-4 border-t border-gray-100">
                  <p class="text-sm text-gray-500 mb-4">上传舌苔照片可帮助医生更准确辨证，建议自然光下拍摄、避免滤镜。</p>
                  
                  <div v-if="tongue_image_url" class="relative inline-block">
                    <img :src="tongue_image_url" class="w-32 h-32 object-cover rounded-lg border border-gray-200" />
                    <div class="absolute -top-2 -right-2 flex space-x-1">
                      <button @click="triggerTongueUpload" class="p-1.5 bg-white rounded-full shadow border hover:bg-gray-50 transition" title="重新拍摄/选取">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </button>
                      <button @click="removeTongueImage" class="p-1.5 bg-white rounded-full shadow border hover:bg-red-50 transition" title="删除">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  <div v-else class="flex items-start space-x-4">
                    <div 
                      class="w-32 h-32 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-emerald-500 hover:text-emerald-600 transition group"
                      @click="triggerTongueUpload"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-8 w-8 text-gray-400 group-hover:text-emerald-500 mb-2 transition" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span class="text-xs text-gray-500 group-hover:text-emerald-600 transition">点击拍摄/选取</span>
                    </div>
                    <div class="flex-1">
                      <div class="text-xs text-gray-500 mb-2">示例图：</div>
                      <img :src="tongue_example_image" class="w-20 h-20 object-cover rounded border border-gray-200" />
                    </div>
                  </div>

                  <!-- 上传进度与状态 -->
                  <div v-if="tongue_uploading" class="mt-4">
                    <div class="flex justify-between text-xs text-gray-500 mb-1">
                      <span>正在上传...</span>
                      <span>{{ tongue_upload_progress }}%</span>
                    </div>
                    <div class="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                      <div class="h-full bg-emerald-500 transition-all duration-300" :style="{width: tongue_upload_progress + '%'}"></div>
                    </div>
                  </div>
                  <div v-if="tongue_upload_error" class="mt-2 text-xs text-red-500 flex items-center space-x-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{{ tongue_upload_error }}</span>
                  </div>
                  
                  <input 
                    type="file" 
                    ref="tongueFileInput" 
                    class="hidden" 
                    accept="image/jpeg,image/png" 
                    @change="handleTongueFileChange" 
                  />
                </div>
              </div>
            </div>
            
            <div class="bg-gray-50 p-6 flex justify-center space-x-4 border-t border-gray-100">
               <button @click="copyDiagnosisRecord" class="px-8 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition shadow-sm font-medium flex items-center">
                 <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                   <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                 </svg>
                 复制问诊记录
               </button>
               <button @click="handleRestart" class="px-8 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition shadow-sm font-medium">
                 返回重新问诊
               </button>
            </div>
          </div>
          
          <p class="text-center text-gray-400 text-sm mb-8">
            免责声明：本系统提供的诊断结果仅供参考，不作为最终医疗依据。请结合专业机构意见进行治疗。
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest } from '@/utils/request';
import tongue_example_image from '@/assets/tongue.jpeg'

export default {
  name: 'SpecializedDiagnosisPanel',
  props: {
    patientId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      currentStep: 1,
      steps: ['选择专科', '智能问诊', '诊断结果'],
      
      // Step 1: Clinic
      clinics: [],
      selectedClinic: null,
      loadingClinics: false,

      // Step 2: Diagnosis
      sessionId: null,
      currentQuestion: null,
      loadingQuestion: false,
      questionCount: 0,
      diagnosisHistory: [], // 用于记录问诊过程
      
      // Step 3: Result
      diagnosisResult: null,
      
      // 舌苔图片上传相关
      is_tongue_upload_expanded: false,
      tongue_image_url: '',
      tongue_image_path: '',
      tongue_example_image,
      tongue_uploading: false,
      tongue_upload_progress: 0,
      tongue_upload_error: ''
    };
  },
  mounted() {
    this.fetchClinics();
  },
  methods: {
    // --- Step 1: Clinic Logic ---
    async fetchClinics() {
      this.loadingClinics = true;
      try {
        // 使用原有的获取疾病列表接口，假设这是公开的
        const response = await httpRequest('get', '/v1/diseases', {
          headers: {
            'Client-Type': '1'
          }
        });
        const payload_data = response && response.data ? response.data : response;
        const clinic_list = payload_data && Array.isArray(payload_data.data) ? payload_data.data : [];
        this.clinics = clinic_list.map((item) => ({
          id: item.code,
          code: item.code,
          name: item.name,
          description: item.description || ''
        }));
      } catch (error) {
        console.error('Failed to fetch clinics:', error);
        // Fallback or error handling
      } finally {
        this.loadingClinics = false;
      }
    },
    selectClinic(clinic) {
      this.selectedClinic = clinic;
    },
    async startDiagnosisProcess() {
      if (!this.selectedClinic || !this.patientId) return;
      
      this.loadingQuestion = true;
      this.currentStep = 2;
      this.questionCount = 0;
      this.diagnosisHistory = []; // 重置问诊记录
      
      try {
        const request_body = {
          disease_code: this.selectedClinic.code || this.selectedClinic.id,
          patient_id: Number(this.patientId)
        };
        
        // 使用用户端专用接口
        const response = await httpRequest('post', '/v1/patient/diagnosis/special/start', {
          headers: {
            'Client-Type': '1'
          },
          body: request_body
        });
        
        const payload_data = response && response.data ? response.data : response;
        this.sessionId = payload_data.session_id;
        this.currentQuestion = this.mapApiQuestion(payload_data.current_question);
        this.questionCount = 1;
        
      } catch (error) {
        console.error('Start diagnosis failed:', error);
        alert('开始问诊失败，请重试');
        this.currentStep = 1;
      } finally {
        this.loadingQuestion = false;
      }
    },

    // --- Step 2: Diagnosis Logic ---
    mapApiQuestion(question_data) {
      if (!question_data) return null;
      const option_list = Array.isArray(question_data.options) ? question_data.options : [];
      return {
        id: question_data.question_code,
        text: question_data.question_text,
        options: option_list.map((opt) => ({
          value: opt.option_code,
          label: opt.option_text
        }))
      };
    },
    
    async submitAnswer(answerValue) {
      // 记录当前问题和答案
      const selectedOption = this.currentQuestion.options.find(opt => opt.value === answerValue);
      if (selectedOption) {
        this.diagnosisHistory.push({
          question: this.currentQuestion.text,
          answer: selectedOption.label
        });
      }

      this.loadingQuestion = true;
      try {
        const request_body = {
          question_code: this.currentQuestion.id,
          selected_options: [answerValue]
        };
        
        // 使用用户端专用接口
        const response = await httpRequest('post', `/v1/patient/diagnosis/special/session/${this.sessionId}/answer`, {
          headers: {
            'Client-Type': '1'
          },
          body: request_body
        });
        
        const payload_data = response && response.data ? response.data : response;
        const status = payload_data.status; // 'next_question' or 'completed'
        
        if (status === 'completed') {
           // 注意：这里假设 completed 时会返回诊断结果，但根据提供的文档，completed 返回的 data 中只有 status 和 session_id
           // 如果文档准确，那么可能需要再次调用获取结果的接口，或者文档中漏掉了结果字段。
           // 查看 SpecializedDiagnosis.vue 的逻辑：
           // if (status_value === 'completed') {
           //   const result_data = payload_data.diagnosis_result || null;
           // }
           // 所以我应该检查 payload_data.diagnosis_result。
           // 如果没有，可能需要额外接口。但鉴于这是一个 Demo，我先假设它在 payload_data 中，或者我会模拟一个结果。
           // 根据文档示例2，completed时确实没有 result。
           // 这可能是一个问题。我先按照文档处理，如果没有结果，可能需要再次查询。
           // 但为了用户体验，我先假设后端会返回结果，或者我需要去 fetch result。
           // 让我们仔细看文档：提交答案文档的 example 2 只有 status 和 session_id。
           // 通常这种情况下，应该有一个 Get Diagnosis Result 的接口。
           // 但是我没有那个接口的文档。
           // 我会检查 payload_data 是否包含 extra data，或者我应该调用之前的接口？
           // 让我们再次确认 SpecializedDiagnosis.vue 是如何处理的。
           // 它直接使用了 payload_data.diagnosis_result。
           // 所以我这里也尝试获取 payload_data.diagnosis_result。
           
           if (payload_data.diagnosis_result) {
              this.diagnosisResult = this.mapDiagnosisResult(payload_data.diagnosis_result);
              this.currentStep = 3;
           } else {
              // 如果没有返回结果，可能是文档没写全，或者是需要另外获取。
              // 这里做一个 fallback，如果真的没有，显示一个提示
              console.warn('No diagnosis result in response');
              this.diagnosisResult = {
                  disease: '诊断完成',
                  advice: '请咨询机构查看详细报告。'
              };
              this.currentStep = 3;
           }
        } else {
           // next question
           this.currentQuestion = this.mapApiQuestion(payload_data.next_question);
           this.questionCount++;
        }
        
      } catch (error) {
        console.error('Submit answer failed:', error);
        alert('提交答案失败，请重试');
        // 如果提交失败，回退历史记录
        this.diagnosisHistory.pop();
      } finally {
        this.loadingQuestion = false;
      }
    },

    async rollbackToPrevQuestion() {
      if (!this.sessionId) return;
      
      this.loadingQuestion = true;
      try {
        // 使用用户端专用回滚接口
        const response = await httpRequest('post', `/v1/patient/diagnosis/special/session/${this.sessionId}/rollback`, {
          headers: {
            'Client-Type': '1'
          }
        });
        
        const payload_data = response && response.data ? response.data : response;
        const status = payload_data.status;
        
        // 更新到上一题
        if (status === 'next_question' && payload_data.next_question) {
          this.currentQuestion = this.mapApiQuestion(payload_data.next_question);
          // 如果 questionCount > 1，则减 1
          if (this.questionCount > 1) {
            this.questionCount--;
            // 回退历史记录
            this.diagnosisHistory.pop();
          }
        }
        
      } catch (error) {
        console.error('Rollback failed:', error);
        alert('回到上一题失败，请重试');
      } finally {
        this.loadingQuestion = false;
      }
    },
    
    mapDiagnosisResult(result_data) {
      if (!result_data) return null;
      
      let symptomGroups = result_data.symptom_groups;
      if (Array.isArray(symptomGroups)) {
        symptomGroups = symptomGroups.join('；');
      }
      
      return {
        disease: result_data.diagnosis_name || '',
        advice: result_data.consultation_summary || result_data.core_pathogenesis || '',
        corePathogenesis: result_data.core_pathogenesis || '',
        recommendedPrescription: result_data.recommended_prescription || ''
      };
    },
    
    handleRestart() {
      this.currentStep = 1;
      this.selectedClinic = null;
      this.sessionId = null;
      this.currentQuestion = null;
      this.questionCount = 0;
      this.diagnosisResult = null;
      this.diagnosisHistory = []; // 重置问诊记录
      
      this.is_tongue_upload_expanded = false;
      this.tongue_image_url = '';
      this.tongue_image_path = '';
      this.tongue_uploading = false;
      this.tongue_upload_progress = 0;
      this.tongue_upload_error = '';
    },

    async copyDiagnosisRecord() {
      if (!this.diagnosisHistory || this.diagnosisHistory.length === 0) {
        alert('暂无问诊记录可复制');
        return;
      }

      let content = `【中医智能问诊记录】\n`;
      content += `专科：${this.selectedClinic?.name || '未知'}\n`;
      content += `时间：${new Date().toLocaleString()}\n`;
      content += `------------------------\n`;
      
      this.diagnosisHistory.forEach((item, index) => {
        content += `${index + 1}. ${item.question}\n   回答：${item.answer}\n`;
      });
      
      content += `------------------------\n`;
      if (this.diagnosisResult) {
        content += `初步诊断：${this.diagnosisResult.disease || '未明确'}\n`;
        content += `健康建议：${this.diagnosisResult.advice || '暂无'}\n`;
      }

      // 尝试使用 navigator.clipboard
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(content);
          alert('问诊记录已复制到剪贴板');
          return;
        } catch (err) {
          console.warn('Clipboard API failed, trying fallback...', err);
        }
      }

      // 降级方案：使用 textarea + execCommand
      try {
        const textArea = document.createElement("textarea");
        textArea.value = content;
        
        // 确保元素不可见但存在于 DOM 中
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        
        textArea.focus();
        textArea.select();
        
        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);
        
        if (successful) {
          alert('问诊记录已复制到剪贴板');
        } else {
          throw new Error('execCommand returned false');
        }
      } catch (err) {
        console.error('Copy failed:', err);
        alert('复制失败，请手动截图保存');
      }
    },
    
    // 舌苔图片上传相关方法
    triggerTongueUpload() {
      this.$refs.tongueFileInput?.click()
    },
    
    removeTongueImage() {
      if (this.tongue_image_url && this.tongue_image_url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.tongue_image_url)
        } catch (e) {
          // ignore
        }
      }
      this.tongue_image_url = ''
      this.tongue_image_path = ''
      if (this.$refs.tongueFileInput) {
        this.$refs.tongueFileInput.value = ''
      }
    },
    
    async handleTongueFileChange(event) {
      const file = event.target.files[0]
      if (!file) return

      this.tongue_upload_error = ''
      this.tongue_upload_progress = 0
      
      // 校验大小 <= 2MB
      if (file.size > 2 * 1024 * 1024) {
        alert('图片大小不能超过2MB')
        event.target.value = ''
        return
      }
      
      // 校验格式
      if (!['image/jpeg', 'image/png'].includes(file.type)) {
        alert('仅支持JPG/PNG格式图片')
        event.target.value = ''
        return
      }
      
      if (this.tongue_image_url && this.tongue_image_url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(this.tongue_image_url)
        } catch (e) {
          // ignore
        }
      }
      this.tongue_image_url = URL.createObjectURL(file)
      
      // 直接上传，不再校验尺寸
      await this.uploadTongueImage(file)
    },
    
    async uploadTongueImage(file, retryCount = 3) {
      this.tongue_uploading = true
      this.tongue_upload_error = ''
      this.tongue_upload_progress = 10 // 初始进度
      
      try {
        const formData = new FormData()
        formData.append('file', file)
        formData.append('scene', 2) // 2: 问诊舌苔
        
        // 模拟进度
        const progressInterval = setInterval(() => {
          if (this.tongue_upload_progress < 90) {
            this.tongue_upload_progress += 10
          }
        }, 200)

        // 使用 fetch 上传
        const token = localStorage.getItem('token') || ''
        const response = await fetch('/api/v1/common/upload', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Client-Type': '1'
          },
          body: formData
        })
        
        clearInterval(progressInterval)
        this.tongue_upload_progress = 100
        
        const resData = await response.json()
        if (resData.code === 2001 && resData.data && resData.data.path) {
          const path = resData.data.path
          this.tongue_image_path = path.startsWith('http') ? path : `/${path}`
          
          // 上传成功后调用更新会话接口
          await this.updateSessionWithTongue()
        } else {
          throw new Error(resData.message || '上传失败')
        }
        
      } catch (error) {
        if (retryCount > 1) {
          this.tongue_upload_error = `上传失败，正在重试...(${4 - retryCount}/3)`
          await new Promise(resolve => setTimeout(resolve, 1000))
          return this.uploadTongueImage(file, retryCount - 1)
        } else {
          this.tongue_upload_error = error.message || '上传失败，错误码：500'
          this.tongue_image_path = ''
          if (this.$refs.tongueFileInput) {
            this.$refs.tongueFileInput.value = ''
          }
        }
      } finally {
        this.tongue_uploading = false
      }
    },
    
    async updateSessionWithTongue() {
      if (!this.sessionId || !this.tongue_image_path) return
      
      try {
        await httpRequest('put', `/v1/patient/diagnosis/special/session/${this.sessionId}`, {
          headers: {
            'Client-Type': '1'
          },
          body: {
            tongue_image: this.tongue_image_path
          }
        })
        alert('图片已上传')
      } catch (error) {
        console.error('更新会话失败:', error)
        alert('舌苔图片已上传，但更新问诊会话失败，请重试')
      }
    }
  }
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
